import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { UserBadge } from '../components/UserBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Users, Plus, Search, ExternalLink, Loader2, Trash2, Pencil, Flame, Zap, Snowflake, Filter } from 'lucide-react';
import { toast } from 'sonner';

// Company name mode when creating a contact
const COMP_FREE  = '__free__';   // user types a free-text company name
const COMP_NONE  = '__none__';   // no company at all

const emptyForm = () => ({
  first_name:'', last_name:'', email:'', phone:'', mobile:'',
  position:'', company_id:'', company_name_free:'',
  notes:'', tags:'',
});

function LeadScoreBadge({ score, label }) {
  if (!score) return null;
  const cls = label === 'Hot' ? 'score-hot' : label === 'Warm' ? 'score-warm' : 'score-cold';
  const Icon = label === 'Hot' ? Flame : label === 'Warm' ? Zap : Snowflake;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>
      <Icon size={10} />{score}
    </span>
  );
}

export default function Contacts() {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  // Track how the user wants to fill the company field
  const [companyMode, setCompanyMode] = useState(COMP_NONE); // 'selected' | COMP_FREE | COMP_NONE

  const fetchContacts = async (q = '', industry = '') => {
    try {
      const res = await axios.get(`${API}/contacts`, { params: {
        ...(q ? { search: q } : {}),
        ...(industry ? { industry } : {}),
      }});
      setContacts(res.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };

  const fetchCompanies = async () => {
    try { setCompanies((await axios.get(`${API}/companies`)).data); } catch { /* silent */ }
  };

  const fetchIndustries = async () => {
    try { setIndustries((await axios.get(`${API}/contacts/industries`)).data); } catch { /* silent */ }
  };

  useEffect(() => { fetchContacts(); fetchCompanies(); fetchIndustries(); }, []);
  useEffect(() => {
    const t = setTimeout(() => fetchContacts(search, industryFilter), 400);
    return () => clearTimeout(t);
  }, [search, industryFilter]);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setCompanyMode(COMP_NONE);
    setShowForm(true);
  };

  const openEdit = (ct) => {
    setEditingId(ct._id || ct.id);
    setForm({
      first_name: ct.first_name || '',
      last_name:  ct.last_name || '',
      email:      ct.email || '',
      phone:      ct.phone || '',
      mobile:     ct.mobile || '',
      position:   ct.position || '',
      company_id: ct.company_id || '',
      company_name_free: ct.company_name_free || '',
      notes:      ct.notes || '',
      tags:       Array.isArray(ct.tags) ? ct.tags.join(', ') : (ct.tags || ''),
    });
    setCompanyMode(
      ct.company_id ? 'selected'
        : ct.company_name_free ? COMP_FREE
        : COMP_NONE
    );
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const tags = form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      const payload = {
        first_name: form.first_name,
        last_name:  form.last_name,
        email:      form.email || null,
        phone:      form.phone || null,
        mobile:     form.mobile || null,
        position:   form.position || null,
        notes:      form.notes || null,
        tags,
      };
      // Company handling based on mode
      if (companyMode === 'selected' && form.company_id) {
        payload.company_id = form.company_id;
        payload.company_name_free = null;
      } else if (companyMode === COMP_FREE) {
        payload.company_id = null;
        payload.company_name_free = form.company_name_free || null;
      } else {
        payload.company_id = null;
        payload.company_name_free = null;
      }
      if (editingId) {
        await axios.put(`${API}/contacts/${editingId}`, payload);
        toast.success('Contatto aggiornato');
      } else {
        await axios.post(`${API}/contacts`, payload);
        toast.success('Contatto creato');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm());
      fetchContacts(search, industryFilter);
      fetchIndustries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare "${name}"?`)) return;
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success('Contatto eliminato');
      setContacts(c => c.filter(ct => ct._id !== id && ct.id !== id));
    } catch { toast.error('Errore'); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{fontFamily:'Manrope, sans-serif'}}>Contatti</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {contacts.length} contatti {industryFilter ? `di categoria "${industryFilter}"` : 'nel database'}
          </p>
        </div>
        {canCreate('contacts') && (
          <Button onClick={openNew} data-testid="add-contact-btn">
            <Plus size={16} className="mr-2" />Nuovo Contatto
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca nome, email, azienda..." className="pl-9" value={search}
            onChange={e => setSearch(e.target.value)} data-testid="contacts-search" />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-muted-foreground" />
          <Select
            value={industryFilter || '__all__'}
            onValueChange={v => setIndustryFilter(v === '__all__' ? '' : v)}
          >
            <SelectTrigger className="w-56 h-9" data-testid="industry-filter">
              <SelectValue placeholder="Tutte le categorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte le categorie</SelectItem>
              {industries.map(ind => (
                <SelectItem key={ind} value={ind}>{ind}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {industryFilter && (
            <Button size="sm" variant="ghost" onClick={() => setIndustryFilter('')} className="h-8 text-xs">
              Rimuovi filtro
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-16">
          <Users size={48} className="text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-1">Nessun contatto</h3>
          {canCreate('contacts') && (
            <Button className="mt-4" onClick={openNew}><Plus size={16} className="mr-2" />Aggiungi Contatto</Button>
          )}
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Contatto</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Azienda</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Categoria</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Posizione</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Score</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Azioni</th>
                </tr>
              </thead>
              <tbody className="stagger-children">
                {contacts.map(ct => {
                  const id = ct._id || ct.id;
                  return (
                    <tr key={id} className="border-b hover:bg-muted/30 transition-colors cursor-pointer" onClick={() => navigate(`/contacts/${id}`)}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-primary">{ct.first_name?.[0]}{ct.last_name?.[0]}</span>
                          </div>
                          <div>
                            <p className="font-medium">{ct.first_name} {ct.last_name}</p>
                            {ct.email && <p className="text-xs text-muted-foreground">{ct.email}</p>}
                            <UserBadge userId={ct.assigned_to} size="xs" />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-muted-foreground text-sm">
                          {ct.company_name || '-'}
                          {!ct.company_id && ct.company_name_free && (
                            <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400 uppercase tracking-wider">(non censita)</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {ct.company_industry ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{ct.company_industry}</span>
                        ) : <span className="text-muted-foreground text-xs">-</span>}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-muted-foreground text-sm">{ct.position || '-'}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <LeadScoreBadge score={ct.lead_score} label={ct.lead_score_label} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                          {canEdit('contacts') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(ct)} data-testid={`edit-contact-${id}`}>
                              <Pencil size={13} />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/contacts/${id}`)}>
                            <ExternalLink size={13} />
                          </Button>
                          {canDelete('contacts') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(id, `${ct.first_name} ${ct.last_name}`)}>
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={showForm} onOpenChange={v => { setShowForm(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Manrope, sans-serif'}}>
              {editingId ? 'Modifica Contatto' : 'Nuovo Contatto'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nome *</Label>
                <Input value={form.first_name} onChange={e => setField('first_name', e.target.value)} required data-testid="contact-first-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Cognome *</Label>
                <Input value={form.last_name} onChange={e => setField('last_name', e.target.value)} required data-testid="contact-last-name" />
              </div>
            </div>

            {/* Company section with 3 modes */}
            <div className="space-y-2 rounded-md border border-dashed border-input p-3 bg-muted/20">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Azienda</Label>
              <div className="flex gap-2 text-xs flex-wrap">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" checked={companyMode === 'selected'}
                    onChange={() => setCompanyMode('selected')} data-testid="company-mode-select" />
                  Seleziona da anagrafica
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" checked={companyMode === COMP_FREE}
                    onChange={() => setCompanyMode(COMP_FREE)} data-testid="company-mode-free" />
                  Digita libero
                </label>
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" checked={companyMode === COMP_NONE}
                    onChange={() => setCompanyMode(COMP_NONE)} data-testid="company-mode-none" />
                  Nessuna
                </label>
              </div>
              {companyMode === 'selected' && (
                <Select value={form.company_id} onValueChange={v => setField('company_id', v)}>
                  <SelectTrigger data-testid="contact-company-select"><SelectValue placeholder="Seleziona azienda..." /></SelectTrigger>
                  <SelectContent>
                    {companies.map(c => <SelectItem key={c._id||c.id} value={c._id||c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {companyMode === COMP_FREE && (
                <Input value={form.company_name_free}
                  onChange={e => setField('company_name_free', e.target.value)}
                  placeholder="Es. Studio Legale Rossi, Comune di Roma..."
                  data-testid="contact-company-free" />
              )}
              {companyMode === COMP_NONE && (
                <p className="text-xs text-muted-foreground italic">Contatto libero senza azienda associata.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Posizione / Qualifica</Label>
              <Input value={form.position} onChange={e => setField('position', e.target.value)}
                placeholder="Es. Direttore acquisti, Titolare..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setField('email', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Telefono fisso</Label>
                <Input value={form.phone} onChange={e => setField('phone', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Cellulare</Label>
              <Input value={form.mobile} onChange={e => setField('mobile', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Tag (separati da virgola)</Label>
              <Input value={form.tags} onChange={e => setField('tags', e.target.value)} placeholder="hot_lead, priorità..." />
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={e => setField('notes', e.target.value)} rows={2} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyForm()); }}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-contact-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingId ? 'Aggiorna' : 'Crea'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
